import React, {useEffect, useState} from "react";
import {GrClose} from "react-icons/gr";
import {toast} from "react-toastify";
import Api from "../../../Api/api";
import {ClipLoader} from "react-spinners";
import {useStateValue} from "../../../states/StateProvider";

const defaultEntry = {
    anrede: "Frau",
    titel: "",
    vorname: "",
    nachname: "",
    position: "",
    email: "",
    durchwahl: "",
    mobil: "",
    privat: "",
    entscheider: "0"
};

const AnsprechpartnerEditorModal = ({company, portal}) => {
    const [, dispatch] = useStateValue();
    const [loading, setLoading] = useState(true);
    const [responsibles, setResponsibles] = useState([]);
    const [selectedRespID, setSelectedRespID] = useState("");
    const [formData, setFormData] = useState({...defaultEntry});
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [activeTab, setActiveTab] = useState("edit");
    const [initialNewFormData, setInitialNewFormData] = useState({...defaultEntry});
    const [newTabFormData, setNewTabFormData] = useState({...defaultEntry});
    const [functionOptions, setFunctionOptions] = useState([]);

    // Helper function to check if a responsible has valid data
    const isValidResponsible = (resp) => {
        // sp_getDataResponsibles returns objects with 'Ansprechpartner' field
        return resp.Ansprechpartner && resp.Ansprechpartner.trim() !== '';
    };

    const handleClose = () => {
        dispatch({type: "SET_ANSPRECHPARTNER_MODAL_OPEN", item: false});
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({...prev, [field]: value}));
        setErrors(prev => ({...prev, [field]: ""}));
        if (activeTab === "new") {
            setNewTabFormData(prev => ({...prev, [field]: value}));
        }
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.vorname.trim()) newErrors.vorname = "Pflichtfeld";
        if (!formData.nachname.trim()) newErrors.nachname = "Pflichtfeld";
        if (!formData.position.trim()) newErrors.position = "Pflichtfeld";
        if (!formData.email.trim()) newErrors.email = "Pflichtfeld";
        return newErrors;
    };

    const saveForm = async () => {
        const newErrors = validate();
        if (Object.keys(newErrors).length) {
            setErrors(newErrors);
            return;
        }

        try {
            setSaving(true);
            await Api().post("/updateResponsible", {
                portal,
                FP_ID: company,
                resp_ID: selectedRespID,
                function: formData.position,
                salutation: formData.anrede,
                title: formData.titel,
                firstname: formData.vorname,
                lastname: formData.nachname,
                email: formData.email,
                phone: formData.durchwahl,
                mobile: formData.mobil,
                privatephone: formData.privat,
                isDecider: formData.entscheider
            });
            toast.success("Ansprechpartner gespeichert.");
            dispatch({ type: 'SET_RESP_CHANGED', item: true });
            await reloadResponsibles();
        } catch (e) {
            console.log(e)
            toast.error("Fehler beim Ansprechpartner Speichern.");
        } finally {
            setSaving(false);
        }
    };

    const createResponsible = async () => {
        const newErrors = validate();
        if (Object.keys(newErrors).length) {
            setErrors(newErrors);
            return;
        }

        try {
            setSaving(true);
            await Api().post("/sp_addResponsible", {
                portal,
                FP_ID: company,
                function: formData.position,
                salutation: formData.anrede,
                title: formData.titel,
                firstname: formData.vorname,
                lastname: formData.nachname,
                email: formData.email,
                phone: formData.durchwahl,
                mobile: formData.mobil,
                privatephone: formData.privat,
                isDecider: formData.entscheider
            });
            toast.success("Neuer Ansprechpartner erstellt.");
            dispatch({ type: 'SET_RESP_CHANGED', item: true });
            setInitialNewFormData({...formData}); // Preserve current state
            await reloadResponsibles();
        } catch (e) {
            console.log(e)
            toast.error("Fehler beim Erstellen.");
        } finally {
            setSaving(false);
        }
    };

    const loadResponsibleData = async (respID) => {
        try {
            setLoading(true);
            const res = await Api().get(`/sp_getResponsible/${portal}/${company}/${respID}`);
            const r = res.data?.[0] || {};
            setFormData({
                anrede: r.salutation || "Frau",
                titel: r.title || "",
                vorname: r.firstname || "",
                nachname: r.lastname || "",
                position: r.function || "",
                email: r.email || "",
                durchwahl: r.phone || "",
                mobil: r.mobile || "",
                privat: r.privatephone || "",
                entscheider: r.isDecider === "1" ? "1" : "0",
            });
        } catch (e) {
            toast.error("Fehler beim Laden der Daten.");
        } finally {
            setLoading(false);
        }
    };

    const changeResponsible = async () => {
        try {
            await Api().post("/sp_changeResponsible", {
                portal, company, responsible_id: selectedRespID
            });
            toast.success("Ansprechpartner wurde gesetzt.");
            dispatch({ type: 'SET_RESP_CHANGED', item: true });
            await reloadResponsibles();
        } catch (e) {
            toast.error("Fehler beim Setzen des Ansprechpartners.");
        }
    };


    const deleteResponsible = async () => {
        const isActive = responsibles.find(r => r.ID === selectedRespID)?.aktiv === "1";
        if (isActive) {
            alert("Dies ist der aktive Ansprechpartner. Bitte setzen Sie zuerst einen anderen als aktiv.");
            return;
        }
        if (!window.confirm("Möchten Sie diesen Ansprechpartner wirklich löschen?")) return;
        try {
            await Api().post("/sp_deleteResponsible", {
                portal, FP_ID: company, resp_ID: selectedRespID
            });
            toast.success("Ansprechpartner gelöscht.");
            dispatch({ type: 'SET_RESP_CHANGED', item: true });
            await reloadResponsibles();
        } catch (e) {
            toast.error("Fehler beim Löschen des Ansprechpartners.");
        }
    };

    const fetchFunctionOptions = async () => {
        try {
            const formData = new FormData();
            formData.append('portal', portal);
            const res = await Api().post("/sp_getDataRespFunctions", formData);
            if (res.data && Array.isArray(res.data)) {
                setFunctionOptions(res.data);
            }
        } catch (e) {
            console.error("Error fetching function options:", e);
            toast.error("Fehler beim Laden der Funktionsoptionen.");
        }
    };

    const reloadResponsibles = async () => {
        try {
            const res = await Api().get(`/sp_getDataResponsibles/${portal}/${company}`);
            const rawList = res.data || [];

            // Filter out responsibles with no valid data
            const list = rawList.filter(isValidResponsible);
            list.sort((a, b) => (b.aktiv === "1" ? 1 : 0) - (a.aktiv === "1" ? 1 : 0));
            setResponsibles(list);

            const active = list.find(r => r.aktiv === "1") || list[0];
            if (active) {
                setSelectedRespID(active.ID);
                await loadResponsibleData(active.ID);
            } else {
                setFormData({...defaultEntry});
                setSelectedRespID("");
            }
        } catch (e) {
            toast.error("Fehler beim Laden der Ansprechpartner.");
        }
    };

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                // Fetch function options
                await fetchFunctionOptions();

                // Fetch responsibles
                const res = await Api().get(`/sp_getDataResponsibles/${portal}/${company}`);
                const rawList = res.data || [];

                // Filter out responsibles with no valid data
                const list = rawList.filter(isValidResponsible);
                list.sort((a, b) => (b.aktiv === "1" ? 1 : 0) - (a.aktiv === "1" ? 1 : 0));
                setResponsibles(list);
                const active = list.find(r => r.aktiv === "1") || list[0];
                if (active) {
                    setSelectedRespID(active.ID);
                    await loadResponsibleData(active.ID);
                }
            } catch (e) {
                toast.error("Fehler beim Laden der Ansprechpartner.");
            } finally {
                setLoading(false);
            }
        };
        fetchInitialData();
    }, []);

    useEffect(() => {
        if (selectedRespID && activeTab === "edit") {
            loadResponsibleData(selectedRespID);
        }
    }, [selectedRespID]);

    return (<div className="fixed inset-0 z-[60] flex items-center justify-center bg-text bg-opacity-50 p-4">
            <div className="relative w-full max-w-3xl bg-white p-6 rounded-lg shadow-lg min-h-[500px]">
                <GrClose className="cursor-pointer absolute top-4 right-4" size="22px" onClick={handleClose}/>
                <h2 className="text-xl font-bold mb-4">Ansprechpartner</h2>

                <div className="flex mb-6 border-b border-gray-300">
                    <button
                        className={`px-4 py-2 text-sm font-medium ${activeTab === "edit" ? "border-b-2 border-mainBlue text-mainBlue" : "text-gray-600"}`}
                        onClick={() => {
                            setActiveTab("edit")
                            if (selectedRespID) {
                                loadResponsibleData(selectedRespID);
                            }
                        }}
                    >
                        Ansprechpartner bearbeiten
                    </button>
                    <button
                        className={`px-4 py-2 text-sm font-medium ${activeTab === "new" ? "border-b-2 border-mainBlue text-mainBlue" : "text-gray-600"}`}
                        onClick={() => {
                            setActiveTab("new");
                            setFormData({...newTabFormData});
                            setErrors({});
                        }}
                    >
                        Neuer Ansprechpartner
                    </button>
                </div>

                {activeTab === "edit" && (<div className="mb-4">
                        <label className="block mb-1 text-sm">Ansprechpartner auswählen</label>
                        <select
                            value={selectedRespID}
                            onChange={(e) => setSelectedRespID(e.target.value)}
                            className="w-full border rounded p-2 text-sm"
                            disabled={responsibles.length === 0}
                        >
                            {responsibles.length === 0 ? (
                                <option value="">zu diesem FirmenProjekt ist kein Ansprechpartner vorhanden</option>
                            ) : (
                                responsibles.map(r => (<option key={r.ID} value={r.ID}>
                                    {r.Ansprechpartner}
                                </option>))
                            )}
                        </select>
                    </div>)}

                {loading ? (<div className="flex justify-center items-center h-40">
                        <ClipLoader size={28} color="#b4b4b4"/>
                    </div>) : (<form className="space-y-4 text-sm">
                        <div className="flex justify-between items-center mb-2">
                            <div className="flex gap-6">
                                <label className="flex items-center space-x-2">
                                    <input
                                        type="radio"
                                        value="0"
                                        checked={formData.entscheider === "0"}
                                        onChange={(e) => handleChange("entscheider", e.target.value)}
                                    />
                                    <span>Ansprechpartner</span>
                                </label>
                                <label className="flex items-center space-x-2">
                                    <input
                                        type="radio"
                                        value="1"
                                        checked={formData.entscheider === "1"}
                                        onChange={(e) => handleChange("entscheider", e.target.value)}
                                    />
                                    <span>Entscheider</span>
                                </label>
                            </div>

                            {responsibles.find(r => r.ID === selectedRespID)?.aktiv === "1" && (<span
                                    className="bg-tahiti text-white text-xs font-semibold px-3 py-1 rounded-full border border-green-300">angezeigt in Übersicht</span>)}
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label>Anrede *</label>
                                <select value={formData.anrede} onChange={(e) => handleChange("anrede", e.target.value)}
                                        className="w-full border rounded p-2">
                                    <option value="Frau">Frau</option>
                                    <option value="Herr">Herr</option>
                                </select>
                            </div>
                            <div>
                                <label>Titel</label>
                                <input value={formData.titel} onChange={(e) => handleChange("titel", e.target.value)}
                                       className="w-full border rounded p-2"/>
                            </div>
                            <div>
                                <label>Vorname *</label>
                                <input value={formData.vorname}
                                       onChange={(e) => handleChange("vorname", e.target.value)}
                                       className={`w-full border rounded p-2 ${errors.vorname ? 'border-red-500' : ''}`}/>
                                {errors.vorname && <p className="text-red-500 text-xs mt-1">{errors.vorname}</p>}
                            </div>
                            <div>
                                <label>Nachname *</label>
                                <input value={formData.nachname}
                                       onChange={(e) => handleChange("nachname", e.target.value)}
                                       className={`w-full border rounded p-2 ${errors.nachname ? 'border-red-500' : ''}`}/>
                                {errors.nachname && <p className="text-red-500 text-xs mt-1">{errors.nachname}</p>}
                            </div>
                            <div>
                                <label>Funktion *</label>
                                <select value={formData.position}
                                        onChange={(e) => handleChange("position", e.target.value)}
                                        className={`w-full border rounded p-2 ${errors.position ? 'border-red-500' : ''}`}>
                                    <option value="">Bitte auswählen...</option>
                                    {functionOptions.map((option, index) => (
                                        <option key={index} value={option.Funktion}>
                                            {option.Funktion}
                                        </option>
                                    ))}
                                </select>
                                {errors.position && <p className="text-red-500 text-xs mt-1">{errors.position}</p>}
                            </div>
                            <div>
                                <label>E-Mail *</label>
                                <input type="email" value={formData.email}
                                       onChange={(e) => handleChange("email", e.target.value)}
                                       className={`w-full border rounded p-2 ${errors.email ? 'border-red-500' : ''}`}/>
                                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                            </div>
                            <div>
                                <label>Durchwahl</label>
                                <input value={formData.durchwahl}
                                       onChange={(e) => handleChange("durchwahl", e.target.value)}
                                       className="w-full border rounded p-2"/>
                            </div>
                            <div>
                                <label>Mobil</label>
                                <input value={formData.mobil} onChange={(e) => handleChange("mobil", e.target.value)}
                                       className="w-full border rounded p-2"/>
                            </div>
                            <div>
                                <label>Telefon Privat</label>
                                <input value={formData.privat} onChange={(e) => handleChange("privat", e.target.value)}
                                       className="w-full border rounded p-2"/>
                            </div>
                        </div>

                        {activeTab === "new" ? (<button type="button" onClick={createResponsible}
                                                        className="w-full mt-6 bg-mainBlue text-white py-2 px-4 rounded hover:bg-blue-700 flex justify-center items-center gap-2"
                                                        disabled={saving}>
                                {saving && <ClipLoader size={16} color="#fff"/>}
                                Neue speichern
                            </button>) : (<button type="button" onClick={saveForm}
                                                  className="w-full mt-6 bg-mainBlue text-white py-2 px-4 rounded hover:bg-blue-700 flex justify-center items-center gap-2"
                                                  disabled={saving}>
                                {saving && <ClipLoader size={16} color="#fff"/>}
                                Änderungen speichern
                            </button>)}

                        {activeTab === "edit" ? (<div className="flex justify-between gap-4 mt-4">
                                {responsibles.find(r => r.ID === selectedRespID)?.aktiv !== "1" && (
                                    <button type="button" onClick={changeResponsible}
                                            className="bg-tahiti text-white py-2 px-4 rounded hover:bg-tahiti w-full">In Übersicht anzeigen</button>)}
                                <button type="button" onClick={deleteResponsible}
                                        className="bg-cancel text-white py-2 px-4 rounded hover:bg-cancel w-full">
                                    Auswahl löschen
                                </button>
                            </div>) : (<button
                                type="button"
                                onClick={() => {
                                    setFormData({...defaultEntry});
                                    setNewTabFormData({...defaultEntry});
                                    setErrors({});
                                }}
                                className="w-full mt-6 bg-cancel text-white py-2 px-4 rounded hover:bg-blue-700 flex justify-center items-center gap-2"
                                disabled={saving}
                            >
                                {saving && <ClipLoader size={16} color="#fff"/>}
                                Form zurücksetzen
                            </button>)}
                    </form>)}
            </div>
        </div>);
};

export default AnsprechpartnerEditorModal;
